import SearchIcon from '@mui/icons-material/Search'
import { Box, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import { useEntitiesFromSearch } from 'api/search'
import React, { useEffect, useMemo, useRef, useState } from 'react'

const ORG_TYPE_MAP: Record<string, { label: string; color: string }> = {
  'Property Management Company': { label: 'PMC', color: 'rgb(93, 79, 251)' },
  'Ownership Group': { label: 'OG', color: 'rgb(92, 179, 253)' },
  'Housing Property': { label: 'HP', color: 'rgb(152, 214, 129)' },
  'Parent Client': { label: 'PC', color: 'rgb(93, 79, 251)' },
  'Agency Whitelabel': { label: 'AW', color: 'rgb(92, 179, 253)' },
  Organization: { label: 'ORG', color: 'rgb(242, 140, 40)' },
  PMC: { label: 'PMC', color: 'rgb(93, 79, 251)' },
  OG: { label: 'OG', color: 'rgb(92, 179, 253)' },
  HP: { label: 'HP', color: 'rgb(152, 214, 129)' },
  PC: { label: 'PC', color: 'rgb(93, 79, 251)' },
  AW: { label: 'AW', color: 'rgb(92, 179, 253)' },
  ORG: { label: 'ORG', color: 'rgb(242, 140, 40)' },
  NT: { label: 'NT', color: 'grey' },
}
const DEFAULT_TYPE = { label: 'NT', color: 'grey' }

const MSA_TYPES = new Set(['MSA', 'RMSA', 'MSA - Housing', 'RMSA - Housing'])
const CSA_TYPES = new Set(['CSA', 'RCSA'])

const DEAL_TYPE_COLORS = {
  msa: 'rgb(88, 188, 128)',
  csa: 'rgb(165, 147, 255)',
  io: 'rgb(242, 180, 60)',
}

interface DealCounts {
  msa: number
  csa: number
  io: number
}

const isDealActive = (d: any, now: Date) => {
  const startS = d.dateRange?.[0] ?? d.startDate
  const endS = d.dateRange?.[1] ?? d.endDate
  const start = startS ? new Date(startS) : undefined
  const end = endS ? new Date(endS) : undefined
  if (start && isNaN(start.getTime())) return true
  if (end && isNaN(end.getTime())) return true
  if (start && now < start) return false
  if (end && now > end) return false
  return true
}

const OrgRow = React.memo(({ org, counts, disabledColor }: { org: any; counts: DealCounts; disabledColor: string }) => {
  const typeInfo = ORG_TYPE_MAP[org.type] || DEFAULT_TYPE
  const hasAny = counts.msa + counts.csa + counts.io > 0

  return (
    <div
      className="org-ref-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 8,
        padding: '3px 4px',
        borderRadius: 4,
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontSize: '0.78rem',
          fontWeight: 500,
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {org.name || '--'}
      </span>
      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: typeInfo.color, textAlign: 'right', minWidth: 28 }}>
        {typeInfo.label}
      </span>
      <span
        style={{
          display: 'inline-grid',
          gridTemplateColumns: '22px 8px 22px 8px 22px',
          fontSize: '0.70rem',
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        <span style={{ color: hasAny ? DEAL_TYPE_COLORS.msa : disabledColor }}>{counts.msa}</span>
        <span style={{ color: disabledColor }}>/</span>
        <span style={{ color: hasAny ? DEAL_TYPE_COLORS.csa : disabledColor }}>{counts.csa}</span>
        <span style={{ color: disabledColor }}>/</span>
        <span style={{ color: hasAny ? DEAL_TYPE_COLORS.io : disabledColor }}>{counts.io}</span>
      </span>
    </div>
  )
})

const fetchOrgs = async (quickFilter: string) => {
  const response = await fetch(`${baseUrl}/search/organizations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      page: 0,
      pageSize: 500,
      sortModel: [{ field: 'name', sort: 'asc' }],
      filterModel: {
        items: [],
        quickFilterValues: quickFilter.trim() ? quickFilter.trim().split(/\s+/) : [],
      },
    }),
  })
  if (!response.ok) throw new Error('Search failed')
  return response.json()
}

const EMPTY_COUNTS: DealCounts = { msa: 0, csa: 0, io: 0 }

const OrganizationReferenceContent: React.FC = () => {
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const { deals = [] } = useEntitiesFromSearch()

  const disabledColor = theme.palette.text.disabled

  // Build PK → { msa, csa, io } counts from active deals
  const dealCountsByPK = useMemo(() => {
    const now = new Date()
    const map = new Map<string, DealCounts>()
    for (const deal of deals) {
      if (!deal?.PK || !isDealActive(deal, now)) continue
      const type = deal.type || ''
      if (!map.has(deal.PK)) map.set(deal.PK, { msa: 0, csa: 0, io: 0 })
      const entry = map.get(deal.PK)!
      if (MSA_TYPES.has(type)) entry.msa++
      else if (CSA_TYPES.has(type)) entry.csa++
      else if (type === 'IO') entry.io++
    }
    return map
  }, [deals])

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: ['search', 'organizations', 'ref-panel', debouncedSearch],
    queryFn: () => fetchOrgs(debouncedSearch),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: prev => prev,
  })

  const rows = data?.rows ?? []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search organizations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.8rem',
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 1,
          color: 'text.primary',
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: theme.palette.background.scrollbar_thumb,
            borderRadius: 3,
          },
          '& .org-ref-row:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          },
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: 8,
            padding: '2px 4px',
            alignItems: 'center',
          }}
        >
          <span />
          <span />
          <span
            style={{
              display: 'inline-grid',
              gridTemplateColumns: '22px 8px 22px 8px 22px',
              fontSize: '0.62rem',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            <span style={{ color: DEAL_TYPE_COLORS.msa }}>MSA</span>
            <span style={{ color: disabledColor }}>/</span>
            <span style={{ color: DEAL_TYPE_COLORS.csa }}>CSA</span>
            <span style={{ color: disabledColor }}>/</span>
            <span style={{ color: DEAL_TYPE_COLORS.io }}>IO</span>
          </span>
        </div>
        {isLoading && rows.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 4 }}>
            Loading...
          </Typography>
        )}
        {!isLoading && rows.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 4 }}>
            No organizations found
          </Typography>
        )}
        {rows.map((org: any, i: number) => (
          <OrgRow
            key={org.PK || `${org.PK}::${org.SK}` || i}
            org={org}
            counts={dealCountsByPK.get(org.PK) ?? EMPTY_COUNTS}
            disabledColor={disabledColor}
          />
        ))}
      </Box>
    </Box>
  )
}

export default OrganizationReferenceContent
